<script lang="ts">
import {Component, Vue} from "vue-facing-decorator";
import ProjectPreviewList from "@/component/ProjectPreviewList.vue";
import NavBar from "@/component/common/NavBar.vue";

@Component({
  components: {
    NavBar,
    ProjectPreviewList
  },
})
export default class HomeView extends Vue {
  private currentSection = 0;
  private isAnimating = false;
  private touchStartY = 0;

  mounted() {
    window.addEventListener('wheel', this.handleWheel, { passive: false });
    window.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    window.addEventListener('touchend', this.handleTouchEnd, { passive: false });
  }

  beforeDestroy() {
    window.removeEventListener('wheel', this.handleWheel);
    window.removeEventListener('touchstart', this.handleTouchStart);
    window.removeEventListener('touchend', this.handleTouchEnd);
  }

  private handleWheel(event: WheelEvent) {
    if (this.isAnimating) return;

    // On bloque le scroll par défaut pour contrôler la transition
    if (Math.abs(event.deltaY) > 20) {
      event.preventDefault();
      if (event.deltaY > 0 && this.currentSection === 0) {
        this.goToSection(1);
      } else if (event.deltaY < 0 && this.currentSection === 1) {
        this.goToSection(0);
      }
    }
  }

  private handleTouchStart(event: TouchEvent) {
    this.touchStartY = event.touches[0].clientY;
  }

  private handleTouchEnd(event: TouchEvent) {
    if (this.isAnimating) return;
    const touchEndY = event.changedTouches[0].clientY;
    const deltaY = this.touchStartY - touchEndY;

    if (Math.abs(deltaY) > 50) {
      if (deltaY > 0 && this.currentSection === 0) {
        this.goToSection(1);
      } else if (deltaY < 0 && this.currentSection === 1) {
        this.goToSection(0);
      }
    }
  }

  public goToSection(index: number) {
    if (this.isAnimating || index === this.currentSection) return;
    this.currentSection = index;
    this.isAnimating = true;
    setTimeout(() => {
      this.isAnimating = false;
    }, 1000); // Correspond à la durée de la transition CSS
  }
}
</script>

<template>
  <NavBar/>
  <div class="home-container">
    <div class="sections-wrapper" :style="{ transform: `translateY(-${currentSection * 100}vh)` }">
      <section class="hero-section">
        <div class="hero-content">
          <div class="hero-image-container">
            <img :src="require('@/assets/image/timothe_photo.jpg')" alt="Timothé Natté" class="hero-image"/>
          </div>
          <div class="hero-text-container">
            <h1 class="hero-name">Timothé <span class="highlight">Natté</span></h1>
            <h2 class="hero-title">Étudiant en BUT Informatique</h2>
            <p class="hero-description">
              Passionné par le développement Full Stack et l'architecture logicielle, je suis un développeur curieux et autonome, toujours prêt à relever des défis techniques pour concevoir des solutions innovantes et performantes.
            </p>
            <div class="scroll-button" @click="goToSection(1)">
              <span>Découvrir mes projets</span>
              <i class="mdi mdi-chevron-down bounce"></i>
            </div>
          </div>
        </div>
      </section>

      <section id="projects" class="projects-section">
        <project-preview-list/>
      </section>
    </div>
  </div>
</template>

<style scoped>
.home-container {
  margin-left: 80px;
  height: 100vh;
  overflow: hidden; /* Bloque le scroll natif */
  background-color: var(--background-color);
}

.sections-wrapper {
  height: 100vh;
  transition: transform 1s cubic-bezier(0.645, 0.045, 0.355, 1); /* Transition fluide et "bloquante" */
  will-change: transform;
}

.hero-section, .projects-section {
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.hero-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 60px;
  max-width: 1200px;
  padding: 40px;
  animation: fadeIn 1.2s ease-out;
}

.hero-image-container {
  width: 350px;
  height: 350px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid var(--primary-color);
  box-shadow: 0 0 30px rgba(19, 124, 139, 0.4);
  flex-shrink: 0;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-text-container {
  flex: 1;
}

.hero-name {
  font-size: 4rem;
  margin: 0;
  color: var(--h1-color);
}

.highlight {
  color: var(--primary-color);
}

.hero-title {
  font-size: 1.8rem;
  color: var(--sub-color);
  margin: 10px 0 20px 0;
}

.hero-description {
  font-size: 1.2rem;
  line-height: 1.6;
  color: var(--text-color);
  margin-bottom: 40px;
  max-width: 600px;
}

.scroll-button {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  color: var(--primary-color);
  transition: transform 0.3s ease;
}

.scroll-button:hover {
  transform: translateY(5px);
}

.scroll-button span {
  font-weight: bold;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.scroll-button i {
  font-size: 2rem;
}

.bounce {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
  40% {transform: translateY(-10px);}
  60% {transform: translateY(-5px);}
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Responsive */
@media (max-width: 1000px) {
  .hero-content {
    flex-direction: column;
    text-align: center;
    gap: 30px;
  }
  .hero-description {
    margin: 0 auto 40px auto;
  }
  .hero-name {
    font-size: 3rem;
  }
}

@media (max-width: 600px) {
  .home-container {
    margin-left: 0;
  }
  .hero-name {
    font-size: 2.2rem;
  }
  .hero-title {
    font-size: 1.4rem;
  }
  .hero-image-container {
    width: 200px;
    height: 200px;
  }
  .hero-content {
    padding: 20px;
  }
}
</style>